// Permission configuration based on the specification
// This file contains all available entities and their corresponding actions

export interface PermissionAction {
  key: string;
  label: string;
  description?: string;
}

export interface PermissionEntity {
  key: string;
  label: string;
  description?: string;
  actions: PermissionAction[];
  category: string;
}

export interface PermissionCategory {
  key: string;
  label: string;
  description?: string;
  entities: PermissionEntity[];
}

// Available actions
export const PERMISSION_ACTIONS: PermissionAction[] = [
  { key: "view", label: "Read", description: "View and read data" },
  { key: "add", label: "Create", description: "Create new records" },
  { key: "edit", label: "Update", description: "Update existing records" },
  { key: "delete", label: "Delete", description: "Delete records" },
  { key: "execute", label: "Execute", description: "Execute operations" },
  { key: "validate", label: "Validate", description: "Validate data" },
];

// All available entities with their supported actions
export const PERMISSION_ENTITIES: PermissionEntity[] = [
  // Auth & Security Management
  {
    key: "auth_tag",
    label: "Tags",
    description: "Authentication tag management",
    category: "auth_security",
    actions: [
      { key: "view", label: "Read", description: "View tags" },
      { key: "add", label: "Create", description: "Create new tags" },
      { key: "edit", label: "Update", description: "Update existing tags" },
      { key: "delete", label: "Delete", description: "Delete tags" },
    ],
  },
  {
    key: "casbin_policies",
    label: "Casbin Policies",
    description: "Casbin policy management",
    category: "auth_security",
    actions: [
      { key: "view", label: "Read", description: "View Casbin policies" },
      {
        key: "add",
        label: "Create",
        description: "Create new Casbin policies",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing Casbin policies",
      },
      { key: "delete", label: "Delete", description: "Delete Casbin policies" },
    ],
  },
  {
    key: "comparison_research_query",
    label: "Comparison Research Queries",
    description: "Comparison research query management",
    category: "rules_queries",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View comparison research queries",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new comparison research queries",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing comparison research queries",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete comparison research queries",
      },
    ],
  },
  {
    key: "blob_list",
    label: "Blob File List",
    description: "Blob file list page access",
    category: "connection_service",
    actions: [
      { key: "view", label: "Read", description: "View blob file list" },
    ],
  },
  {
    key: "sftp_list",
    label: "SFTP File List",
    description: "SFTP file list page access",
    category: "connection_service",
    actions: [
      { key: "view", label: "Read", description: "View SFTP file list" },
    ],
  },
  {
    key: "connection_key",
    label: "Connection Keys",
    description: "Connection key management",
    category: "connection_service",
    actions: [
      { key: "view", label: "Read", description: "View connection keys" },
      {
        key: "add",
        label: "Create",
        description: "Create new connection keys",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing connection keys",
      },
      { key: "delete", label: "Delete", description: "Delete connection keys" },
    ],
  },
  {
    key: "connection_runtime_config",
    label: "Connection Runtime Config",
    description: "Connection runtime configuration management",
    category: "connection_service",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View connection runtime configurations",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new connection runtime configurations",
      },
    ],
  },

  // Domain & Resource Management
  {
    key: "domain",
    label: "Domains",
    description: "Domain management",
    category: "domain_resource",
    actions: [
      { key: "view", label: "Read", description: "View domains" },
      { key: "add", label: "Create", description: "Create new domains" },
      { key: "edit", label: "Update", description: "Update existing domains" },
      { key: "delete", label: "Delete", description: "Delete domains" },
    ],
  },
  {
    key: "domain_linkage",
    label: "Domain Linkages",
    description: "Domain linkage management",
    category: "domain_resource",
    actions: [
      { key: "view", label: "Read", description: "View domain linkages" },
      {
        key: "add",
        label: "Create",
        description: "Create new domain linkages",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing domain linkages",
      },
    ],
  },

  {
    key: "execution_checkpoints",
    label: "Execution Checkpoints",
    description: "Execution checkpoint management",
    category: "results_execution",
    actions: [
      { key: "view", label: "Read", description: "View execution checkpoints" },
    ],
  },

  // System & Configuration Management
  {
    key: "file_processing_attributes",
    label: "File Processing Attributes",
    description: "File processing attributes management",
    category: "system_config",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View file processing attributes",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new file processing attributes",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing file processing attributes",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete file processing attributes",
      },
    ],
  },
  {
    key: "generic_research_query",
    label: "Generic Research Queries",
    description: "Generic research query management",
    category: "rules_queries",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View generic research queries",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new generic research queries",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing generic research queries",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete generic research queries",
      },
      {
        key: "execute",
        label: "Execute",
        description: "Execute generic research queries",
      },
    ],
  },
  {
    key: "generic_research_query_result",
    label: "Generic Research Query Results",
    description: "Generic research query result management",
    category: "results_execution",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View generic research query results",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete generic research query results",
      },
    ],
  },
  {
    key: "comparison_research_query_result",
    label: "Comparison Research Query Results",
    description: "Comparison research query result management",
    category: "results_execution",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View comparison research query results",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete comparison research query results",
      },
    ],
  },
  {
    key: "research_query_scoring_config",
    label: "Research Query Scoring Config",
    description: "Research query scoring configuration management",
    category: "scoring_config",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View research query scoring configs",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new research query scoring configs",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing research query scoring configs",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete research query scoring configs",
      },
    ],
  },

  // Issue Management
  {
    key: "rule_execution_issues",
    label: "Rule Execution Issues",
    description: "Rule execution issue management",
    category: "issue_management",
    actions: [
      { key: "view", label: "Read", description: "View rule execution issues" },
      {
        key: "edit",
        label: "Update",
        description: "Update rule execution issues",
      },
    ],
  },
  {
    key: "validation_issues",
    label: "Validation Issues",
    description: "Validation issue management",
    category: "issue_management",
    actions: [
      { key: "view", label: "Read", description: "View validation issues" },
      { key: "edit", label: "Update", description: "Update validation issues" },
    ],
  },
  {
    key: "issue_status",
    label: "Issue Status",
    description: "Issue status management",
    category: "issue_management",
    actions: [
      { key: "view", label: "Read", description: "View issue status" },
      { key: "add", label: "Create", description: "Create new issue status" },
      { key: "edit", label: "Update", description: "Update issue status" },
    ],
  },
  {
    key: "linked_service",
    label: "Linked Services",
    description: "Linked service management",
    category: "connection_service",
    actions: [
      { key: "view", label: "Read", description: "View linked services" },
      {
        key: "add",
        label: "Create",
        description: "Create new linked services",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing linked services",
      },
      { key: "delete", label: "Delete", description: "Delete linked services" },
    ],
  },
  {
    key: "research_query_execution_issues",
    label: "Research Query Execution Issues",
    description: "Research query execution issue management",
    category: "issue_management",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View research query execution issues",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update research query execution issues",
      },
    ],
  },
  {
    key: "resource",
    label: "Resources",
    description: "Resource management",
    category: "domain_resource",
    actions: [
      { key: "view", label: "Read", description: "View resources" },
      { key: "add", label: "Create", description: "Create new resources" },
      {
        key: "edit",
        label: "Update",
        description: "Update existing resources",
      },
      { key: "delete", label: "Delete", description: "Delete resources" },
      { key: "validate", label: "Validate", description: "Validate resources" },
    ],
  },
  {
    key: "resource_column_details",
    label: "Resource Column Details",
    description: "Resource column details management",
    category: "domain_resource",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View resource column details",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new resource column details",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing resource column details",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete resource column details",
      },
    ],
  },

  // Rules & Queries Management
  {
    key: "rule",
    label: "Rules",
    description: "Rule management",
    category: "rules_queries",
    actions: [
      { key: "view", label: "Read", description: "View rules" },
      { key: "add", label: "Create", description: "Create new rules" },
      { key: "edit", label: "Update", description: "Update existing rules" },
      { key: "delete", label: "Delete", description: "Delete rules" },
      { key: "execute", label: "Execute", description: "Execute rules" },
    ],
  },
  {
    key: "rule_result",
    label: "Rule Results",
    description: "Rule result management",
    category: "results_execution",
    actions: [
      { key: "view", label: "Read", description: "View rule results" },
      { key: "delete", label: "Delete", description: "Delete rule results" },
    ],
  },
  {
    key: "run_instance",
    label: "Execution Groups",
    description: "Run instance management",
    category: "system_config",
    actions: [
      { key: "view", label: "Read", description: "View execution groups" },
      {
        key: "add",
        label: "Create",
        description: "Create new execution groups",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing execution groups",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete execution groups",
      },
    ],
  },
  {
    key: "system",
    label: "Systems",
    description: "System management",
    category: "system_config",
    actions: [
      { key: "view", label: "Read", description: "View systems" },
      { key: "add", label: "Create", description: "Create new system records" },
      { key: "edit", label: "Update", description: "Update existing systems" },
      { key: "delete", label: "Delete", description: "Delete systems" },
    ],
  },
  {
    key: "issue_classification",
    label: "Issue Classification",
    description: "Issue classification management",
    category: "system_config",
    actions: [
      { key: "view", label: "Read", description: "View issue classifications" },
      {
        key: "add",
        label: "Create",
        description: "Create new issue classifications",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing issue classifications",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete issue classifications",
      },
    ],
  },
  {
    key: "issue_reason",
    label: "Issue Reason",
    description: "Issue reason management",
    category: "system_config",
    actions: [
      { key: "view", label: "Read", description: "View issue reasons" },
      {
        key: "add",
        label: "Create",
        description: "Create new issue reasons",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing issue reasons",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete issue reasons",
      },
    ],
  },
  {
    key: "execution_sub_group",
    label: "Execution Sub Group",
    description: "Execution sub-group management",
    category: "system_config",
    actions: [
      { key: "view", label: "Read", description: "View execution sub-groups" },
      {
        key: "add",
        label: "Create",
        description: "Create new execution sub-groups",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing execution sub-groups",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete execution sub-groups",
      },
    ],
  },

  // User & Role Management
  {
    key: "user",
    label: "Users",
    description: "User management",
    category: "user_management",
    actions: [
      { key: "view", label: "Read", description: "View users" },
      { key: "edit", label: "Update", description: "Update existing users" },
      { key: "delete", label: "Delete", description: "Delete users" },
    ],
  },
  {
    key: "user_auth_tags",
    label: "User-Tag Associations",
    description: "User tag assignment management",
    category: "user_management",
    actions: [
      { key: "view", label: "Read", description: "View user tag associations" },
      {
        key: "edit",
        label: "Update",
        description: "Update user tag assignments",
      },
    ],
  },
  {
    key: "user_group",
    label: "User Groups",
    description: "User group management",
    category: "user_management",
    actions: [
      { key: "view", label: "Read", description: "View user groups" },
      { key: "add", label: "Create", description: "Create new user groups" },
      { key: "edit", label: "Update", description: "Update user groups" },
      { key: "delete", label: "Delete", description: "Delete user groups" },
    ],
  },
  {
    key: "user_group_auth_tags",
    label: "Group-Tag Associations",
    description: "Group tag assignment management",
    category: "user_management",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View group-tag associations",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update group tag assignments",
      },
    ],
  },
  {
    key: "user_group_roles",
    label: "Group-Role Associations",
    description: "Group role assignment management",
    category: "user_management",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View group-role associations",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update group role assignments",
      },
    ],
  },
  {
    key: "user_group_users",
    label: "User-Group Associations",
    description: "User group assignment management",
    category: "user_management",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View user-group associations",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update user group assignments",
      },
    ],
  },
  {
    key: "user_role",
    label: "User Roles",
    description: "User role management",
    category: "user_management",
    actions: [
      { key: "view", label: "Read", description: "View user roles" },
      { key: "add", label: "Create", description: "Create new user roles" },
      { key: "edit", label: "Update", description: "Update user roles" },
      { key: "delete", label: "Delete", description: "Delete user roles" },
    ],
  },
  {
    key: "user_systems",
    label: "User-System Associations",
    description: "User system assignment management",
    category: "user_management",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View user-system associations",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update user system assignments",
      },
    ],
  },
  {
    key: "user_user_roles",
    label: "User-Role Associations",
    description: "User role assignment management",
    category: "user_management",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View user-role associations",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update user role assignments",
      },
    ],
  },
  {
    key: "validation_research_query",
    label: "Validation Research Queries",
    description: "Validation research query management",
    category: "rules_queries",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View validation research queries",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new validation research queries",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing validation research queries",
      },
    ],
  },
  // Scoring Configuration Management
  {
    key: "validation_scoring_config",
    label: "Validation Scoring Config",
    description: "Validation scoring configuration management",
    category: "scoring_config",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View validation scoring configs",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new validation scoring configs",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing validation scoring configs",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete validation scoring configs",
      },
    ],
  },
  {
    key: "comparison_scoring_config",
    label: "Comparison Scoring Config",
    description: "Comparison scoring configuration management",
    category: "scoring_config",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View comparison scoring configs",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new comparison scoring configs",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing comparison scoring configs",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete comparison scoring configs",
      },
    ],
  },
  {
    key: "comparison_run_scoring_config",
    label: "Comparison Run Scoring Config",
    description: "Comparison run scoring configuration management",
    category: "scoring_config",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View comparison run scoring configs",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new comparison run scoring configs",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing comparison run scoring configs",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete comparison run scoring configs",
      },
    ],
  },
  {
    key: "validation_run_scoring_config",
    label: "Validation Run Scoring Config",
    description: "Validation run scoring configuration management",
    category: "scoring_config",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View validation run scoring configs",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new validation run scoring configs",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing validation run scoring configs",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete validation run scoring configs",
      },
    ],
  },
  {
    key: "research_query_run_scoring_config",
    label: "Research Query Run Scoring Config",
    description: "Research query run scoring configuration management",
    category: "scoring_config",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View research query run scoring configs",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new research query run scoring configs",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing research query run scoring configs",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete research query run scoring configs",
      },
    ],
  },
  {
    key: "run_scoring_pipeline_weight_config",
    label: "Run Scoring Pipeline Weight Config",
    description: "Run scoring pipeline weight configuration management",
    category: "scoring_config",
    actions: [
      {
        key: "view",
        label: "Read",
        description: "View run scoring pipeline weight configs",
      },
      {
        key: "add",
        label: "Create",
        description: "Create new run scoring pipeline weight configs",
      },
      {
        key: "edit",
        label: "Update",
        description: "Update existing run scoring pipeline weight configs",
      },
      {
        key: "delete",
        label: "Delete",
        description: "Delete run scoring pipeline weight configs",
      },
    ],
  },
  {
    key: "scoring_result",
    label: "Scoring Results",
    description: "Scoring result management",
    category: "results_execution",
    actions: [
      { key: "view", label: "Read", description: "View scoring results" },
    ],
  },
  {
    key: "validation_result",
    label: "Validation Results",
    description: "Validation result management",
    category: "results_execution",
    actions: [
      { key: "view", label: "Read", description: "View validation results" },
      {
        key: "delete",
        label: "Delete",
        description: "Delete validation results",
      },
    ],
  },
];

// Permission categories for grouping entities
export const PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    key: "auth_security",
    label: "Authentication & Security",
    description: "Authentication, authorization, and security management",
    entities: PERMISSION_ENTITIES.filter(
      (entity) => entity.category === "auth_security",
    ),
  },
  {
    key: "user_management",
    label: "User Management",
    description: "User, role, and group management",
    entities: PERMISSION_ENTITIES.filter(
      (entity) => entity.category === "user_management",
    ),
  },
  {
    key: "domain_resource",
    label: "Domain & Resource Management",
    description: "Domain and resource configuration",
    entities: PERMISSION_ENTITIES.filter(
      (entity) => entity.category === "domain_resource",
    ),
  },
  {
    key: "rules_queries",
    label: "Rules & Queries",
    description: "Rule and query management",
    entities: PERMISSION_ENTITIES.filter(
      (entity) => entity.category === "rules_queries",
    ),
  },
  {
    key: "connection_service",
    label: "Connections & Services",
    description: "Connection and service management",
    entities: PERMISSION_ENTITIES.filter(
      (entity) => entity.category === "connection_service",
    ),
  },
  {
    key: "system_config",
    label: "System Configuration",
    description: "System and configuration management",
    entities: PERMISSION_ENTITIES.filter(
      (entity) => entity.category === "system_config",
    ),
  },
  {
    key: "results_execution",
    label: "Results & Execution",
    description: "Result viewing and execution management",
    entities: PERMISSION_ENTITIES.filter(
      (entity) => entity.category === "results_execution",
    ),
  },
  {
    key: "issue_management",
    label: "Issue Management",
    description: "Issue tracking and management",
    entities: PERMISSION_ENTITIES.filter(
      (entity) => entity.category === "issue_management",
    ),
  },
  {
    key: "scoring_config",
    label: "Scoring Configuration",
    description: "Scoring configuration management",
    entities: PERMISSION_ENTITIES.filter(
      (entity) => entity.category === "scoring_config",
    ),
  },
];

// Utility functions
export const getEntityByKey = (key: string): PermissionEntity | undefined => {
  return PERMISSION_ENTITIES.find((entity) => entity.key === key);
};

export const getCategoryByKey = (
  key: string,
): PermissionCategory | undefined => {
  return PERMISSION_CATEGORIES.find((category) => category.key === key);
};

export const getActionByKey = (key: string): PermissionAction | undefined => {
  return PERMISSION_ACTIONS.find((action) => action.key === key);
};

export const getAllPermissionKeys = (): string[] => {
  const permissions: string[] = [];
  PERMISSION_ENTITIES.forEach((entity) => {
    entity.actions.forEach((action) => {
      permissions.push(`${entity.key}:${action.key}`);
    });
  });
  return permissions;
};

export const parsePermissionKey = (
  permissionKey: string,
): { entity: string; action: string } | null => {
  const parts = permissionKey.split(":");
  if (parts.length !== 2) return null;
  return { entity: parts[0], action: parts[1] };
};
